import { useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Modal,
  Select,
  Text,
} from "@shopify/polaris";
import {
  createStockPublication,
  publicationRequest,
  type PublicationSelection,
} from "../../../../packages/shared/stock-publication/client";
import {
  queueAccountKey,
  type QueueAccount,
} from "../../../../packages/shared/shopify-queue/client";
import { ProductSelection } from "./ProductSelection";
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
const commands = new Map<string, ReturnType<typeof createStockPublication>>();
const storage = {
  get length() {
    return window.localStorage.length;
  },
  key: (i: number) => window.localStorage.key(i),
  getItem: (key: string) => window.localStorage.getItem(key),
  setItem: (key: string, value: string) =>
    window.localStorage.setItem(key, value),
  removeItem: (key: string) => window.localStorage.removeItem(key),
};
type Preview = {
  ready: boolean;
  reasons: string[];
  preview_hash: string;
  product_count: number;
  warehouse_count: number;
  location_count: number;
  store: string | null;
  connection_id: string | null;
  canQueue: boolean;
};
export function PublicationPanel({
  account,
  onQueued,
  canQueue,
}: {
  account: QueueAccount;
  onQueued: () => void;
  canQueue: boolean;
}) {
  const [params] = useSearchParams();
  const key = queueAccountKey(account);
  if (!commands.has(key))
    commands.set(key, createStockPublication(base, account, storage));
  const command = commands.get(key)!;
  const state = useSyncExternalStore(command.subscribe, command.snapshot),
    [selection, setSelection] = useState<PublicationSelection>(() => {
      const raw = params.get("product_ids"),
        ids = raw?.split(",").map(Number) || [],
        store = params.has("connection_id") ? {connection_id: params.get("connection_id")!} : {};
      return raw &&
        ids.length <= 500 &&
        ids.every((id) => Number.isSafeInteger(id) && id > 0) &&
        new Set(ids).size === ids.length
        ? { ...store, mode: "selected", product_ids: ids }
        : { ...store, mode: "all", product_ids: [] };
    }),
    [review, setReview] = useState<Preview | null>(null),
    [reviewing, setReviewing] = useState(false),
    [error, setError] = useState("");
  const [stores, setStores] = useState<{id: string; domain: string}[] | null>(null),
    [storesError, setStoresError] = useState(""),
    [storeRefresh, setStoreRefresh] = useState(0);
  useEffect(() => {
    const abort = new AbortController();
    setStores(null); setStoresError(""); setReview(null);
    void publicationRequest<{stores: {id: string; domain: string}[]}>(base, account, "/stores", {signal: abort.signal})
      .then(data => {
        if (abort.signal.aborted) return;
        setStores(data.stores);
        setSelection(current => current.connection_id !== undefined || data.stores.length !== 1 ? current : {...current, connection_id: data.stores[0].id});
      })
      .catch(e => { if (!abort.signal.aborted) setStoresError(e.message); });
    return () => abort.abort();
  }, [account, storeRefresh]);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const locked = state.busy || !!state.pending || reviewing;
  const prepare = async () => {
    setReviewing(true);
    setError("");
    try {
      const data = await publicationRequest<Preview>(
        base,
        account,
        `/preview?connection_id=${encodeURIComponent(selection.connection_id!)}&mode=${selection.mode}${selection.mode === "selected" ? "&product_ids=" + selection.product_ids.join(",") : ""}`,
      );
      if (alive.current) setReview(data);
    } catch (e) {
      if (alive.current)
        setError(e instanceof Error ? e.message : "Could not load the review");
    } finally {
      if (alive.current) setReviewing(false);
    }
  };
  const confirm = async () => {
    if (!review || !review.ready || !review.canQueue || locked) return;
    const saved = review;
    setReview(null);
    await command.submit(selection, saved.preview_hash, saved.product_count, saved.store);
    if (alive.current && command.snapshot().success) onQueued();
  };
  const retry = async () => {
    await command.retry();
    if (alive.current && command.snapshot().success) onQueued();
  };
  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Publish warehouse stock
        </Text>
        <Text as="p">
          Send current available stock to your chosen Shopify store. All
          mapped products includes products with zero stock. Shopify updates run
          in the background.
        </Text>
        {(error || state.error) && (
          <Banner tone="critical">{error || state.error}</Banner>
        )}
        {state.success && (
          <Banner tone="success">
            {state.success.products_queued} products queued{state.success.store ? ` for ${state.success.store}` : ""}.{" "}
            {state.success.new_jobs} new updates; {state.success.joined_jobs}{" "}
            joined waiting updates. This is a saved queue receipt, not
            confirmation from Shopify.
          </Banner>
        )}
        {state.pending && (
          <Banner tone="warning">
            <BlockStack gap="200">
              <Text as="p">
                A saved publication for {state.pending.count} products{state.pending.store ? ` to ${state.pending.store}` : ""} needs a
                confirmed reply. It stays saved after you close the browser.
              </Text>
              <Button loading={state.busy} onClick={() => void retry()}>
                Retry saved publication
              </Button>
            </BlockStack>
          </Banner>
        )}
        {!canQueue && (
          <Text as="p">
            You can view results. Permission to send inventory updates is needed
            to queue stock.
          </Text>
        )}
        {storesError && <Banner tone="critical">{storesError}</Banner>}
        {!stores && !storesError && <Text as="p">Loading Shopify stores…</Text>}
        {stores?.length === 0 && <Text as="p">Connect a Shopify store before sending stock.</Text>}
        <Select label="Shopify store" value={selection.connection_id || ""}
          disabled={locked || !canQueue || !stores?.length}
          options={[{label: "Choose a Shopify store", value: ""}, ...(stores || []).map(store => ({label: store.domain, value: store.id}))]}
          onChange={connection_id => {setReview(null); setSelection({connection_id, mode: selection.mode, product_ids: []});}} />
        <Button disabled={locked} onClick={() => setStoreRefresh(value => value + 1)}>Refresh stores</Button>
        <Select
          label="Products to publish"
          value={selection.mode}
          disabled={locked || !canQueue}
          options={[
            { label: "All mapped products", value: "all" },
            { label: "Choose products", value: "selected" },
          ]}
          onChange={(mode) =>
            setSelection({ ...selection, mode: mode as "all" | "selected", product_ids: [] })
          }
        />
        {selection.mode === "selected" && selection.connection_id && (
          <ProductSelection
            key={selection.connection_id}
            connectionId={selection.connection_id}
            account={account}
            selected={selection.product_ids}
            onChange={(product_ids) =>
              setSelection({ ...selection, mode: "selected", product_ids })
            }
            disabled={locked || !canQueue}
          />
        )}
        <Button
          loading={reviewing}
          disabled={
            locked ||
            !stores?.some(store => store.id === selection.connection_id) ||
            !canQueue ||
            (selection.mode === "selected" && !selection.product_ids.length)
          }
          onClick={() => void prepare()}
        >
          Review stock publication
        </Button>
        <Modal
          title="Review stock publication"
          open={!!review}
          onClose={() => setReview(null)}
          primaryAction={{
            content: "Queue stock publication",
            disabled: locked || !review?.ready || !review.canQueue,
            onAction: () => void confirm(),
          }}
          secondaryActions={[
            { content: "Cancel", onAction: () => setReview(null) },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              {review && (
                <>
                  <Text as="p">
                    {review.product_count} mapped products will be queued for{" "}
                    {review.store || "the connected store"} across{" "}
                    {review.warehouse_count} warehouses and{" "}
                    {review.location_count} Shopify locations.
                  </Text>
                  {review.reasons.map((reason) => (
                    <Banner key={reason} tone="warning">
                      {reason}
                    </Banner>
                  ))}
                  <Text as="p">
                    When each update runs, it sends current available stock
                    after reservations. A zero quantity clears available stock
                    in Shopify. This does not change physical warehouse stock.
                  </Text>
                  <Text as="p">
                    Already waiting updates are reused. Check the saved receipt
                    and Shopify update results after queuing.
                  </Text>
                </>
              )}
            </BlockStack>
          </Modal.Section>
        </Modal>
      </BlockStack>
    </Card>
  );
}
